import type { OmitProperties, DeepPartial } from 'ts-essentials'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type { WithId, CreateEntityDto } from '@/types'

import type {
  Entity,
  Projection,
  Paging,
  Paged,
  Filter,
  PartialEntity,
  SortCriteria,
} from './BaseService.types'

type PartialOrDeepPartial<T> = Partial<T> | DeepPartial<T>

export default class BaseService<
  T extends Entity,
  TCascade extends Partial<Entity> = T,
  F extends Filter = Filter
> {
  constructor(
    protected readonly client: AxiosInstance,
    protected readonly entity: string
  ) {}

  async list<R extends Partial<T> = T>(
    config?: AxiosRequestConfig
  ): Promise<R[]>
  async list<R extends Partial<T> = T>(
    paging: Paging,
    config?: AxiosRequestConfig
  ): Promise<Paged<R[]>>
  async list(arg1?: AxiosRequestConfig | Paging, config?: AxiosRequestConfig) {
    let cfg: AxiosRequestConfig = {}
    if (this.isPaging(arg1)) {
      cfg = config ?? {}
      this.paging(cfg, arg1)
    } else {
      cfg = arg1 ?? {}
    }
    const { data } = await this.client.get(`/${this.entity}`, cfg)
    return data
  }

  async get<R extends PartialOrDeepPartial<T> = T>(
    id: T['id'],
    config?: AxiosRequestConfig
  ): Promise<R> {
    const { data } = await this.client.get(`/${this.entity}/${id}`, config)
    return data
  }

  async create(
    payload:
      | CreateEntityDto<OmitProperties<T, Entity>>
      | CreateEntityDto<TCascade>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const { data } = await this.client.post<T>(
      `/${this.entity}`,
      payload,
      config
    )
    return data
  }

  async update(payload: WithId<Partial<T> | Partial<TCascade>>): Promise<T> {
    const { data } = await this.client.patch<T>(
      `/${this.entity}/${payload.id}`,
      payload
    )
    return data
  }

  async delete(id: T['id']): Promise<T['id']> {
    await this.client.delete(`/${this.entity}/${id}`)
    return id
  }

  async deleteMany(ids: T['id'][]): Promise<T['id'][]> {
    const { data } = await this.client.delete<T['id'][]>(
      `/${this.entity}/bulk`,
      { params: { ids } }
    )
    return data
  }

  async search<U extends Projection<T>>(
    filter: F,
    projection: U,
    config?: AxiosRequestConfig
  ): Promise<PartialEntity<T, U>[]>
  async search<U extends Projection<T>>(
    filter: F,
    projection: U,
    paging: Paging,
    sorting?: SortCriteria[],
    config?: AxiosRequestConfig
  ): Promise<Paged<PartialEntity<T, U>[]>>
  async search<U extends Projection<T>>(
    filter: F,
    projection: U,
    paging: null,
    sorting?: SortCriteria[],
    config?: AxiosRequestConfig
  ): Promise<PartialEntity<T, U>[]>
  async search<U extends Projection<T>>(
    filter: F,
    projection: U,
    arg3?: AxiosRequestConfig | Paging | null,
    arg4?: AxiosRequestConfig | SortCriteria[],
    arg5?: AxiosRequestConfig
  ): Promise<PartialEntity<T, U>[] | Paged<PartialEntity<T, U>[]>> {
    let cfg: AxiosRequestConfig = {}
    if (this.isPaging(arg3)) {
      if (this.isSorting(arg4)) {
        cfg = arg5 ?? {}
      } else {
        cfg = arg4 ?? {}
      }
    } else {
      cfg = arg3 ?? {}
    }
    if (this.isSorting(arg4)) {
      this.sorting(cfg, arg4)
    }
    this.enrich(cfg, projection)
    const params = cfg.params ?? {}
    const search: Record<string, unknown> = {}
    if (filter.query !== undefined && filter.query !== '') {
      search.name = {
        $contL: filter.query,
      }
    }
    if (filter.tags !== undefined && filter.tags.length > 0) {
      search.tags = {
        $contArr: filter.tags,
      }
    }
    this.s(filter, search)
    if (Object.keys(search).length > 0) {
      params.s = search
    }
    cfg.params = params
    return this.isPaging(arg3) ? this.list(arg3, cfg) : this.list(cfg)
  }

  protected enrich(config: AxiosRequestConfig, projection: Projection<T>) {
    const fields: string[] = []
    const join: string[] = []
    for (const [key, value] of Object.entries(projection)) {
      if (typeof value === 'boolean') {
        fields.push(key)
        continue
      }
      join.push(...this.join(key, value))
    }

    const params = config.params ?? {}
    params.fields = fields.join(',')
    if (join.length > 0) {
      params.join = join.join(',')
    }
    config.params = params
  }

  protected isPaging(paging: unknown): paging is Paging {
    if (typeof paging !== 'object') return false
    if (paging === null) return false
    return (
      typeof (paging as Paging).limit === 'number' &&
      typeof (paging as Paging).page === 'number'
    )
  }

  protected isSorting(sorting: unknown): sorting is SortCriteria[] {
    return Array.isArray(sorting)
  }

  protected paging(config: AxiosRequestConfig, paging: Paging | null) {
    if (paging === null) return config
    const params = config.params ?? {}
    params.limit = paging.limit
    params.page = paging.page
    config.params = params
  }

  protected sorting(config: AxiosRequestConfig, sorting: SortCriteria[]) {
    const params = config.params ?? {}
    const value: string[] = []
    for (const criteria of sorting) {
      value.push(`${criteria.field},${criteria.direction}`)
    }
    params.sort = value
    config.params = params
  }

  private join(relation: string, projection: Projection<T>): string[] {
    const join: string[] = []
    for (const [key, value] of Object.entries(projection)) {
      if (typeof value === 'boolean') {
        join.push(`${relation}||${key}`)
        continue
      }
      join.push(...this.join(key, value))
    }
    return join
  }

  protected s(_: F, __: Record<string, unknown>): void {
    // meant to be implemented by child classes
  }
}
